import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../utils/api";
import { MdClose, MdCheckCircle, MdError, MdMailOutline, MdExpandLess, MdExpandMore } from "react-icons/md";
import { FiLoader } from "react-icons/fi";

const DELAY_MS = 3000;

const EmailSendQueue = ({ queue, setQueue }) => {
    const [collapsed, setCollapsed] = useState(false);
    const runningRef = useRef(false);
    const queueRef = useRef(queue);

    // keep our authoritative mirror in sync with the latest prop on every render
    useEffect(() => {
        queueRef.current = queue;
    }, [queue]);

    useEffect(() => {
        const hasPending = queue.some((item) => item.status === "pending");
        if (runningRef.current || !hasPending) return;

        runningRef.current = true;

        const setItemStatus = (id, patch) => {
            queueRef.current = queueRef.current.map((i) =>
                i.id === id ? { ...i, ...patch } : i
            );
            setQueue((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
        };

        const run = async () => {
            while (true) {
                const target = queueRef.current.find((i) => i.status === "pending");
                if (!target) break;

                setItemStatus(target.id, { status: "sending" });

                try {
                    await axiosInstance.post(`/stand/send-email/${target.id}`);
                    setItemStatus(target.id, { status: "success" });
                } catch (err) {
                    setItemStatus(target.id, {
                        status: "error",
                        message: err?.response?.data?.message || "Failed to send",
                    });
                }

                await new Promise((resolve) => setTimeout(resolve, DELAY_MS));
            }

            runningRef.current = false;
        };

        run();
    }, [queue, setQueue]);

    if (!queue.length) return null;

    const pendingCount = queue.filter((i) => i.status === "pending" || i.status === "sending").length;
    const doneCount = queue.length - pendingCount;

    const clearFinished = () =>
        setQueue((prev) => prev.filter((i) => i.status === "pending" || i.status === "sending"));

    const dismiss = () => setQueue([]);

    return (
        <div className="fixed bottom-4 right-4 w-80 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 text-white">
                <div className="flex items-center gap-2 text-sm font-medium">
                    <MdMailOutline />
                    {pendingCount > 0
                        ? `Sending emails (${doneCount}/${queue.length})`
                        : `Emails sent (${queue.length})`}
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => setCollapsed((c) => !c)} className="p-1 hover:bg-white/10 rounded">
                        {collapsed ? <MdExpandLess /> : <MdExpandMore />}
                    </button>
                    <button
                        onClick={pendingCount > 0 ? clearFinished : dismiss}
                        className="p-1 hover:bg-white/10 rounded"
                        title={pendingCount > 0 ? "Clear finished" : "Close"}
                    >
                        <MdClose />
                    </button>
                </div>
            </div>

            {!collapsed && (
                <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                    {queue.map((item) => (
                        <div key={item.id} className="flex items-center gap-2.5 px-4 py-2.5 text-sm">
                            <span className="shrink-0">
                                {item.status === "pending" && (
                                    <span className="w-2 h-2 rounded-full bg-slate-300 inline-block" />
                                )}
                                {item.status === "sending" && <FiLoader className="w-4 h-4 text-blue-500 animate-spin" />}
                                {item.status === "success" && <MdCheckCircle className="w-4 h-4 text-emerald-500" />}
                                {item.status === "error" && <MdError className="w-4 h-4 text-rose-500" />}
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-slate-800 font-medium">
                                    {item.exhibitorName || item.standNumber}
                                </p>
                                <p className="text-xs text-slate-400 truncate">
                                    {item.status === "error"
                                        ? item.message
                                        : item.isResend && item.status !== "success"
                                            ? `Resending — ${item.standNumber}`
                                            : item.standNumber}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default EmailSendQueue;